import { FC, ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, styled } from "@mui/material";

import { Header } from "../Header";
import Footer from "../Footer";
import ContentWrapper from "../ContentWrapper";
import { BOTTOM_NAV_CLEARANCE, BottomNavBar } from "../BottomNavBar";
import useAuthorization from "../../contexts/useAuthorization";
import { useLocation } from "react-router";
import { matchRoute } from "../../config/app.routes";
import { APP_TITLE } from "../../utils/constants";

type LayoutProps = {
  children: ReactNode | undefined;
};

export const Layout: FC<LayoutProps> = ({ children }) => {
  const { fetchingUser } = useAuthorization();
  // const [open, setOpen] = useState(false);
  // const toggleNavigation = () => setOpen((status) => !status);
  const location = useLocation();
  const match = matchRoute(location.pathname);
  const isLandPage = location.pathname.startsWith("/asset/");
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const [child, setChild] = useState<any>(null);

  useEffect(() => {
    if (match) {
      document.title = match.title + " | " + APP_TITLE;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!fetchingUser) {
      setChild(children);
    }
  }, [fetchingUser]);

  useLayoutEffect(() => {
    const element = headerRef.current;

    if (!element) {
      return;
    }

    const updateHeaderHeight = () => {
      setHeaderHeight(Math.ceil(element.getBoundingClientRect().height));
    };

    updateHeaderHeight();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(element);

    return () => observer.disconnect();
  }, [location.pathname, fetchingUser]);

  const white = location.pathname === "/help" || location.pathname === "/help/";
  const isExplorePage = location.pathname === "/explore" || location.pathname === "/explore/";
  const isShuftiComplete = location.pathname === "/shufti-complete" || location.pathname === "/shufti-complete/";

  if (isShuftiComplete) {
    return <>{child}</>;
  }

  // if (location.pathname === '/resource-center') {
  //   return children as any || null;
  // }

  // The bottom nav sits at the same fragment slot in both branches, so it survives the Explore <->
  // everything-else wrapper swap and its indicator can slide between those tabs instead of remounting.
  if (isExplorePage) {
    return (
      <>
        <ExploreLayoutWrapper sx={{ backgroundColor: white ? "#fff" : "#ccc" }}>
          <Box
            id="eco-explore-header-wrap"
            component="header"
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
          >
            <Header />
          </Box>
          <Box component="main" sx={{ flexGrow: 1, display: "flex", position: "relative", zIndex: 0 }}>
            {child}
          </Box>
          {/* <Box component="footer" sx={{ zIndex: 1001 }}>
          <Footer />
        </Box> */}
        </ExploreLayoutWrapper>
        <BottomNavBar />
      </>
    );
  }

  return (
    <>
      <LayoutWrapper
        sx={{
          backgroundColor: white ? "#fff" : "#ccc",
          "--layout-header-height": `${headerHeight}px`,
        }}
      >
        <Box
          ref={headerRef}
          component="header"
          sx={
            isLandPage
              ? {
                  position: "sticky",
                  top: 0,
                  zIndex: (theme) => theme.zIndex.appBar,
                }
              : undefined
          }
        >
          <Header />
        </Box>
        <Box component="main" sx={{ flexGrow: 1, display: "flex" }}>
          <ContentWrapper>{child}</ContentWrapper>
        </Box>
        <Box component="footer" sx={{ position: "relative", zIndex: 3 }}>
          <Footer />
        </Box>
      </LayoutWrapper>
      <BottomNavBar />
    </>
  );
};

const LayoutWrapper = styled("div")(() => ({
  minHeight: "100vh",
  padding: 0,
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  background: "#ccc",
  // No bottom padding for the bottom nav here: the Footer reserves that space inside its own
  // black block, so the page doesn't end in an empty strip under the floating bar.
}));

const ExploreLayoutWrapper = styled("div")(({ theme }) => ({
  minHeight: "100vh",
  margin: "0 auto",
  padding: 0,
  display: "flex",
  flexDirection: "column",
  background: "#ccc",
  overflow: "hidden",
  position: "relative",
  [theme.breakpoints.down("md")]: {
    paddingBottom: BOTTOM_NAV_CLEARANCE,
  },
}));
